import React, { useState } from "react";
import styles from "./style.module.scss";
import { ChartOptions, Plugin } from "chart.js";
import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";

import { Scatter } from "react-chartjs-2";
import "chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm";
import annotationPlugin from "chartjs-plugin-annotation";
import AnnotationPluginOptions from "chartjs-plugin-annotation";
import { ChartData, OneSeriesObject } from "../../../../types/index";

ChartJS.register(
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  annotationPlugin,
);

interface ScatterChartProps {
  chartData: ChartData;
  chartTitle: string;
  yTitle: string;
  xLimits: (number | undefined)[];
  yLimits: (number | undefined)[];
  ySteps: number;
  yZoomLimits: number[];
  warningLine: number | null;
}

export default function ScatterChart(props: ScatterChartProps) {
  const {
    chartData,
    chartTitle,
    yTitle,
    xLimits,
    yLimits,
    ySteps,
    warningLine,
  } = props;

  const [plugins] = useState<Plugin<"scatter">[]>([]);
  const isEmpty = !chartData.datasets.some(
    (x: OneSeriesObject) => x.data.length > 0,
  );
  if (isEmpty) {
    return <div className={styles.noDataWarning}>No Data</div>;
  }

  const annotations: ChartOptions<"scatter"> = {
    plugins: {
      annotation: {
        annotations: warningLine
          ? [
              {
                type: "line",
                yMin: warningLine ? warningLine : 0,
                yMax: warningLine ? warningLine : 0,
                borderColor: "white",
                borderWidth: 1,
                borderDash: [10, 5],
              },
            ]
          : [],
      },
    },
  };

  const options: ChartOptions<"scatter"> & {
    annotation?: typeof AnnotationPluginOptions;
  } = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        title: {
          display: yTitle ? true : false,
          text: yTitle,
          color: "white",
        },
        beginAtZero: false,
        min: yLimits ? yLimits[0] : undefined,
        max: yLimits ? yLimits[1] : undefined,
        grid: { display: true, color: "grey" },
        ticks: {
          color: "white",
          stepSize: ySteps,
        },
      },
      x: {
        display: true,
        type: "time",
        min: xLimits[0],
        max: xLimits[1],
        time: {
          unit: "minute",
          displayFormats: {
            minute: "h:mm a",
          },
        },
        ticks: {
          color: "white",
          stepSize: 5,
          maxRotation: 45,
          minRotation: 15,
        },
      },
    },
    animation: { duration: 0 },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: { labels: { color: "white" } },
      title: {
        display: chartTitle ? true : false,
        text: chartTitle,
        color: "white",
      },
      ...annotations,
    },
  };

  return (
    <Scatter
      className={styles.chart}
      options={options}
      data={chartData}
      plugins={plugins}
    />
  );
}
